import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import {
  Nav,
  Footer,
  ClosingV5,
  Eyebrow,
  PillarAccent,
} from "../App";
import { useContactPanel } from "../components/ContactPanel";

/* ============================================================
   /ecommerce-marketing-agency-v2 — Sunday Digital
   Variation B — chkstepan.com-inspired editorial method stack.
   Identical to MarketingPage everywhere EXCEPT the "Our Method"
   section, which uses alternating light/dark sticky cards with
   inline giant numerals and italic "(intention)" descriptors,
   following the chkstepan reference more literally.
============================================================ */

const PrimaryCTA = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
  >
    {children}
    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </button>
);

/* ============================================================
   01 · Hero — full-circle rotating ring (identical to A)
============================================================ */
const SERVICE_NODES = [
  "Strategy",
  "Content",
  "Social",
  "Email",
  "Influencer",
  "Paid Ads",
  "SEO",
  "CRO",
];

const FullCircleRing = () => {
  const radius = 200;
  const cx = 250;
  const cy = 250;
  return (
    <div className="relative w-full aspect-square max-w-[28rem] mx-auto">
      <motion.svg
        viewBox="0 0 500 500"
        className="absolute inset-0 w-full h-full"
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ duration: 70, ease: "linear", repeat: Infinity }}
        style={{ originX: "50%", originY: "50%" }}
      >
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="1" />
        <circle cx={cx} cy={cy} r={radius - 30} fill="none" stroke="rgba(244,214,78,0.35)" strokeWidth="1" strokeDasharray="2 6" />
        <circle cx={cx} cy={cy} r={radius - 70} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        {SERVICE_NODES.map((label, i) => {
          const angle = (i / SERVICE_NODES.length) * Math.PI * 2 - Math.PI / 2;
          const x = cx + Math.cos(angle) * radius;
          const y = cy + Math.sin(angle) * radius;
          return (
            <g key={label}>
              <circle cx={x} cy={y} r="6" fill="#F4D64E" />
              <circle cx={x} cy={y} r="11" fill="none" stroke="rgba(244,214,78,0.4)" strokeWidth="1" />
            </g>
          );
        })}
      </motion.svg>

      <motion.div
        className="absolute inset-0"
        initial={{ rotate: 0 }}
        animate={{ rotate: -360 }}
        transition={{ duration: 70, ease: "linear", repeat: Infinity }}
      >
        {SERVICE_NODES.map((label, i) => {
          const angle = (i / SERVICE_NODES.length) * Math.PI * 2 - Math.PI / 2;
          const labelRadiusPct = 47;
          const x = 50 + Math.cos(angle) * labelRadiusPct;
          const y = 50 + Math.sin(angle) * labelRadiusPct;
          return (
            <span
              key={label}
              className="absolute -translate-x-1/2 -translate-y-1/2 text-[0.6rem] sm:text-[0.65rem] font-bold tracking-[0.22em] uppercase text-neutral-300 whitespace-nowrap"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <motion.span
                className="inline-block"
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 70, ease: "linear", repeat: Infinity }}
              >
                {label}
              </motion.span>
            </span>
          );
        })}
      </motion.div>

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center">
          <div className="font-display text-[2.5rem] md:text-[3rem] font-black text-[#F4D64E] tabular-nums leading-none">
            360°
          </div>
          <div className="mt-2 text-[0.65rem] font-bold tracking-[0.22em] uppercase text-neutral-400">
            Full circle
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketingHero = ({ onPrimary }) => (
  <section
    id="top"
    data-testid="marketing-hero-v2"
    className="relative overflow-hidden bg-[#0B0F14]"
  >
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background: "radial-gradient(900px 600px at 80% 30%, rgba(244,214,78,0.10), transparent 60%)",
      }}
    />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-24">
      <div
        data-reveal="eyebrow"
        className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-10 md:mb-14"
      >
        <a href="/" className="hover:text-[#F4D64E] transition-colors">Sunday</a>
        <span className="w-1 h-1 rounded-full bg-neutral-700" />
        <span className="text-[#F4D64E]">eCommerce Marketing · v2</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-16 items-center">
        <div className="lg:col-span-7">
          <span data-reveal="eyebrow">
            <Eyebrow>360 Digital Marketing</Eyebrow>
          </span>
          <h1
            data-testid="marketing-hero-title-v2"
            className="font-display mt-7 text-[2.5rem] sm:text-[3.5rem] md:text-[4.25rem] lg:text-[4.75rem] font-black tracking-[-0.035em] leading-[0.98] text-white"
          >
            <span data-reveal="mask-block">
              <span>Full circle.</span>
            </span>
            <span data-reveal="mask-block" style={{ "--i": 1 }} className="block">
              <span>
                Built for{" "}
                <span className="italic font-normal text-neutral-400">growth</span>
                <PillarAccent>
                  <span className="opacity-0 select-none">.</span>
                </PillarAccent>
                <span className="text-white">.</span>
              </span>
            </span>
          </h1>
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="mt-9 max-w-[52ch] text-[1.02rem] md:text-[1.15rem] text-neutral-300 leading-[1.6]"
          >
            <span>
              A boutique marketing team turning ideas into traffic, traffic into
              customers, and customers into community — strategy, creative,
              media, and analytics under one roof.
            </span>
          </p>
          <div
            data-reveal="eyebrow"
            style={{ "--i": 3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <PrimaryCTA testId="mkt-hero-cta-v2" onClick={onPrimary}>
              Start a Project
            </PrimaryCTA>
            <a
              href="#services"
              className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/45 hover:bg-[#F4D64E]/10 hover:border-[#F4D64E] px-6 py-4 rounded-md transition-colors"
            >
              See services
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>

        <div className="lg:col-span-5">
          <FullCircleRing />
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   02 · Trust strip
============================================================ */
const TrustStripMkt = () => (
  <section className="relative bg-[#0B0F14] border-y border-white/[0.05]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 md:py-7">
      <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
        <span>In-house team</span>
        <span className="hidden md:inline w-1 h-1 rounded-full bg-neutral-700" />
        <span>Data-led</span>
        <span className="hidden md:inline w-1 h-1 rounded-full bg-neutral-700" />
        <span>360° stack</span>
        <span className="hidden md:inline w-1 h-1 rounded-full bg-neutral-700" />
        <span>10+ years</span>
        <span className="hidden md:inline w-1 h-1 rounded-full bg-neutral-700" />
        <span className="text-[#F4D64E]">Brooklyn · Remote</span>
      </div>
    </div>
  </section>
);

/* ============================================================
   03 · Why us
============================================================ */
const WhyUs = () => (
  <section data-testid="why-us-v2" className="relative bg-[#0B0F14]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-12 mb-12 md:mb-16">
        <div className="lg:col-span-5">
          <span data-reveal="eyebrow">
            <Eyebrow>The Sunday way</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.25rem] md:text-[3rem] lg:text-[3.5rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
          >
            <span>
              Strategy meets{" "}
              <span className="italic font-normal text-neutral-400">execution</span>.{" "}
              <PillarAccent>Data</PillarAccent> meets craft.
            </span>
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        <div data-reveal="mask-block" style={{ "--i": 0 }}>
          <h3 className="font-display text-[1.25rem] md:text-[1.4rem] font-black tracking-[-0.015em] text-white leading-[1.2] mb-4">
            The plan, never the gimmick.
          </h3>
          <p className="text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.65] max-w-[52ch]">
            Data-driven strategies that align with your business goals and
            audience behaviour. Every move is built for measurable growth and
            long-term impact — actionable, not aspirational.
          </p>
        </div>
        <div data-reveal="mask-block" style={{ "--i": 1 }}>
          <h3 className="font-display text-[1.25rem] md:text-[1.4rem] font-black tracking-[-0.015em] text-white leading-[1.2] mb-4">
            Channels that compound.
          </h3>
          <p className="text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.65] max-w-[52ch]">
            Email, paid, organic, and creative — calibrated together so each
            channel fuels the next. Content that informs, inspires, and sells.
            Campaigns built to be measured, not just launched.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   04 · Method — chkstepan-style alternating editorial cards (V2)
   Differences from V1:
     • Alternating dark / cream cards (chkstepan literal feel)
     • Big inline numeral on the LEFT (chkstepan placement)
     • Italic "( intention )" descriptor at the bottom-right
     • Soft visual thumbnail per card
     • Subtle pin stack: cards translate up and fade slightly
       (no aggressive 3D rotateX) for a calmer editorial feel
============================================================ */
const METHOD_V2 = [
  {
    n: "01",
    eyebrow: "Listen",
    title: "We learn your business before we build for it.",
    body: "Workshops, audits, customer interviews, analytics deep-dives. Nothing gets shipped until we understand what's actually moving the needle.",
    intention: "( it's intention )",
    img: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=900&q=70",
    theme: "dark",
  },
  {
    n: "02",
    eyebrow: "Strategize",
    title: "A roadmap with milestones, not just opinions.",
    body: "Channel mix, budget allocation, content calendar, and the experiments we'll run. Decisions documented before a dollar is spent.",
    intention: "( every L is data )",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=900&q=70",
    theme: "cream",
  },
  {
    n: "03",
    eyebrow: "Create",
    title: "Creative that earns the click — and the second one.",
    body: "Copy, design, video, and motion built around the strategy. No template ads, no recycled hooks. Made-for-channel from the start.",
    intention: "( crafted, not stamped )",
    img: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=900&q=70",
    theme: "dark",
  },
  {
    n: "04",
    eyebrow: "Launch",
    title: "Coordinated rollouts across every active channel.",
    body: "Paid, organic, email, influencer, lifecycle — orchestrated to fire together. Tracking and attribution wired in from day one.",
    intention: "( in lockstep )",
    img: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=900&q=70",
    theme: "cream",
  },
  {
    n: "05",
    eyebrow: "Measure",
    title: "Performance dashboards your team can actually read.",
    body: "Weekly reviews, monthly deep-dives. We focus on the numbers that compound — LTV, CAC, return rate, repeat purchase — not vanity reach.",
    intention: "( signal over noise )",
    img: "https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=900&q=70",
    theme: "dark",
  },
  {
    n: "06",
    eyebrow: "Iterate",
    title: "Optimize, double down, kill what isn't working.",
    body: "We run our own experiments and report results candidly. The plan changes — what doesn't is the discipline of measuring before deciding.",
    intention: "( choose the right problem )",
    img: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?auto=format&fit=crop&w=900&q=70",
    theme: "cream",
  },
];

const MethodCardV2 = ({ step, i, total }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Calmer pin-stack — translate up, mild fade. No rotateX.
  const opacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.85, 0.55]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.96]);

  const isCream = step.theme === "cream";
  const bgClass = isCream
    ? "bg-[#F4EFE6]"
    : "bg-gradient-to-br from-[#11171F] to-[#0D131A]";
  const borderClass = isCream ? "border-black/[0.06]" : "border-white/[0.08]";
  const titleClass = isCream ? "text-neutral-950" : "text-white";
  const bodyClass = isCream ? "text-neutral-700" : "text-neutral-300";
  const muteClass = isCream ? "text-neutral-500" : "text-neutral-500";
  const accentClass = isCream ? "text-neutral-950" : "text-[#F4D64E]";
  const numeralClass = isCream
    ? "text-neutral-950/[0.08]"
    : "text-[#F4D64E]/[0.10]";
  const dividerClass = isCream ? "bg-black/[0.08]" : "bg-white/[0.08]";
  const intentionClass = isCream ? "text-neutral-500" : "text-neutral-500";
  const learnMoreClass = isCream
    ? "text-neutral-950 hover:text-neutral-700"
    : "text-[#F4D64E] hover:text-white";

  return (
    <div
      ref={ref}
      className="relative bg-[#0B0F14] py-10 lg:py-0 lg:sticky lg:top-0 lg:h-screen lg:flex lg:items-center"
      style={{ zIndex: i + 1 }}
    >
      <motion.div
        style={
          typeof window !== "undefined" && window.innerWidth >= 1024
            ? { opacity, y, scale }
            : undefined
        }
        className="w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-10"
      >
        <div
          className={`relative rounded-2xl lg:rounded-3xl border ${borderClass} ${bgClass} overflow-hidden shadow-[0_60px_120px_-40px_rgba(0,0,0,0.85)] lg:min-h-[78vh] flex`}
        >
          {/* GIANT inline numeral — TOP-LEFT (chkstepan placement) */}
          <div
            aria-hidden
            className="absolute -top-6 sm:-top-10 left-0 pointer-events-none select-none leading-none"
          >
            <span
              className={`block font-display font-black tracking-[-0.06em] leading-[0.78] ${numeralClass}`}
              style={{ fontSize: "clamp(10rem, 22vw, 22rem)" }}
            >
              {step.n}
            </span>
          </div>

          {/* Subtle grain overlay on cream cards for editorial warmth */}
          {isCream && (
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-[0.35]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.04) 1px, transparent 0)",
                backgroundSize: "3px 3px",
              }}
            />
          )}

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-y-0 w-full">
            {/* LEFT — copy block */}
            <div className="lg:col-span-7 p-7 sm:p-9 md:p-12 lg:p-14 lg:pr-10 flex flex-col justify-between gap-7 lg:gap-9 min-h-full relative z-10">
              {/* Top eyebrow row */}
              <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                <span className={`text-[0.65rem] sm:text-[0.7rem] font-bold tracking-[0.22em] uppercase ${accentClass} tabular-nums`}>
                  {step.n} / {String(total).padStart(2, "0")}
                </span>
                <span className={`hidden sm:inline h-px flex-1 ${dividerClass} max-w-[100px]`} />
                <span className={`text-[0.65rem] sm:text-[0.7rem] font-bold tracking-[0.22em] uppercase ${muteClass}`}>
                  {step.eyebrow}
                </span>
              </div>

              {/* Title + body */}
              <div className="flex flex-col gap-7 lg:gap-9">
                <h3 className={`font-display text-[1.7rem] sm:text-[2.1rem] md:text-[2.6rem] lg:text-[3.25rem] xl:text-[3.65rem] font-black tracking-[-0.04em] leading-[1.0] ${titleClass}`}>
                  {step.title}
                </h3>
                <p className={`text-[0.95rem] md:text-[1.05rem] lg:text-[1.15rem] ${bodyClass} leading-[1.6] max-w-[56ch]`}>
                  {step.body}
                </p>
              </div>

              {/* Bottom row — Learn more + intention italic */}
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <a
                  href="#contact"
                  className={`inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.2em] uppercase ${learnMoreClass} transition-colors`}
                >
                  Learn more
                  <ArrowUpRight className="w-4 h-4" />
                </a>
                <span className={`italic ${intentionClass} text-[0.85rem] md:text-[0.95rem] tracking-tight`}>
                  {step.intention}
                </span>
              </div>
            </div>

            {/* RIGHT — visual panel */}
            <div className="lg:col-span-5 relative min-h-[18rem] lg:min-h-full overflow-hidden">
              <img
                src={step.img}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Theme-aware tint over imagery */}
              <span
                aria-hidden
                className={`absolute inset-0 ${
                  isCream
                    ? "bg-gradient-to-l from-transparent via-[#F4EFE6]/30 to-[#F4EFE6]/70"
                    : "bg-gradient-to-l from-transparent via-[#0D131A]/30 to-[#0D131A]/80"
                }`}
              />
              {/* Subtle yellow ambient corner */}
              <span
                aria-hidden
                className="absolute top-0 right-0 w-44 h-44 rounded-full bg-[#F4D64E]/[0.18] blur-3xl pointer-events-none"
              />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const MethodIntro = () => (
  <section className="relative bg-[#0B0F14] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="max-w-4xl">
        <span data-reveal="eyebrow">
          <Eyebrow>Our method · v2</Eyebrow>
        </span>
        <h2
          data-reveal="mask-block"
          style={{ "--i": 1 }}
          className="mt-7 font-display text-[2.5rem] md:text-[4rem] lg:text-[4.75rem] font-black tracking-[-0.04em] leading-[0.98] text-white"
        >
          <span>
            Six steps,{" "}
            <span className="italic font-normal text-neutral-400">measured</span>{" "}
            <PillarAccent>every week</PillarAccent>.
          </span>
        </h2>
        <p
          data-reveal="mask-block"
          style={{ "--i": 2 }}
          className="mt-10 max-w-[60ch] text-[1.05rem] md:text-[1.15rem] text-neutral-400 leading-[1.65]"
        >
          <span>
            The way we run every engagement — same rhythm whether we&apos;re
            shipping a 2-week campaign or a multi-quarter growth program.
          </span>
        </p>
      </div>
    </div>
  </section>
);

const MethodV2 = () => (
  <section data-testid="method-v2" className="relative bg-[#0B0F14]">
    <MethodIntro />
    <div className="relative">
      {METHOD_V2.map((s, i) => (
        <MethodCardV2 key={s.n} step={s} i={i} total={METHOD_V2.length} />
      ))}
    </div>
    <div className="hidden lg:block lg:h-[20vh]" />
  </section>
);

/* ============================================================
   05 · Services grid (identical to A)
============================================================ */
const SERVICES = [
  { n: "01", title: "Strategy & Consulting", body: "Data-driven strategies that align with your business goals — every move built for measurable growth and long-term impact.", img: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=70" },
  { n: "02", title: "Content Creation", body: "Engaging product visuals, copy, and digital assets that enhance the buying journey and boost conversions.", img: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=1200&q=70" },
  { n: "03", title: "Social Media", body: "Strategic content calendars and targeted campaigns that turn followers into loyal customers and revenue.", img: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=1200&q=70" },
  { n: "04", title: "Email Marketing", body: "Targeted, automated, personalized flows — from welcome series to lifecycle. Segmentation that maximizes engagement.", img: "https://images.unsplash.com/photo-1633265486064-086b219458ec?auto=format&fit=crop&w=1200&q=70" },
  { n: "05", title: "Influencer & Collabs", body: "Authentic voice partnerships that boost engagement, build trust, and convert followers into loyal customers.", img: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?auto=format&fit=crop&w=1200&q=70" },
  { n: "06", title: "Paid Marketing", body: "Targeted Meta, TikTok, Pinterest, Google Search & Shopping campaigns — precise audiences, optimized creatives, measurable ROI.", img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=70" },
  { n: "07", title: "SEO", body: "Comprehensive audits and competitive analysis — optimize every stage of the funnel and drive qualified traffic that converts.", img: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?auto=format&fit=crop&w=1200&q=70" },
  { n: "08", title: "Conversion Optimization", body: "User-behaviour analysis, iterative testing, and journey optimization. Turn more visitors into buyers with data-driven changes.", img: "https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=1200&q=70" },
];

const ServicesGrid = () => (
  <section
    id="services"
    data-testid="services-v2"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-12 mb-16 md:mb-20">
        <div className="lg:col-span-5">
          <span data-reveal="eyebrow">
            <Eyebrow>Eight disciplines, one team</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.25rem] md:text-[3rem] lg:text-[3.5rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
          >
            <span>
              Every channel{" "}
              <span className="italic font-normal text-neutral-400">that</span>{" "}
              <PillarAccent>moves metrics</PillarAccent>.
            </span>
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 self-end">
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65]"
          >
            <span>
              Strategy, creative, and media — calibrated together so each
              channel makes the next one work harder.
            </span>
          </p>
        </div>
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16 md:gap-y-20">
        {SERVICES.map((s, i) => (
          <li
            key={s.n}
            data-reveal="mask-block"
            style={{ "--i": Math.min(i, 5) }}
            data-testid={`service-v2-${s.n}`}
            className={`group ${i % 2 === 1 ? "md:translate-y-16" : ""}`}
          >
            <a href="#contact" className="block">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0F141A] mb-7 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:border-[#F4D64E]/30">
                <img
                  src={s.img}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                />
                <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/80 via-[#0B0F14]/20 to-transparent transition-opacity duration-500 group-hover:from-[#0B0F14]/60" />
                <span aria-hidden className="absolute inset-0 ring-0 group-hover:ring-1 ring-inset ring-[#F4D64E]/20 rounded-2xl transition-all duration-500" />
                <span className="absolute top-5 left-5 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] tabular-nums">
                  {s.n}
                </span>
                <span className="absolute top-5 right-5 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
              <h3 className="font-display text-[1.45rem] md:text-[1.65rem] lg:text-[1.85rem] font-black tracking-[-0.02em] leading-[1.15] text-white group-hover:text-[#F4D64E] transition-colors mb-3">
                {s.title}
              </h3>
              <p className="text-[0.98rem] md:text-[1.02rem] text-neutral-400 leading-[1.6] max-w-[52ch]">
                {s.body}
              </p>
            </a>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ============================================================
   06 · Showreel band (identical to A)
============================================================ */
const SHOWREEL_VIDEO =
  "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4";

const Showreel = () => (
  <section
    data-testid="showreel-v2"
    className="relative overflow-hidden bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="relative aspect-[16/10] md:aspect-[21/9] lg:aspect-[24/9] w-full">
      <video
        src={SHOWREEL_VIDEO}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=2000&q=70"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/92 via-[#0B0F14]/30 to-transparent" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-[#0B0F14]/30 via-transparent to-[#0B0F14]/40" />

      <div className="absolute inset-0 flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
          <div className="max-w-3xl">
            <span data-reveal="eyebrow">
              <Eyebrow>Behind the work</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-7 font-display text-[2rem] md:text-[3.25rem] lg:text-[4.25rem] font-black tracking-[-0.035em] leading-[1.0] text-white"
            >
              <span>
                We move metrics —{" "}
                <span className="italic font-normal text-neutral-300">not</span>{" "}
                <PillarAccent>vanity numbers</PillarAccent>.
              </span>
            </h2>
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-7 max-w-[52ch] text-[1rem] md:text-[1.1rem] text-neutral-300 leading-[1.6]"
            >
              <span>
                Strategy, creative, paid media, and analytics calibrated
                together so the numbers that matter — LTV, CAC, repeat
                purchase, return rate — actually compound.
              </span>
            </p>
          </div>
        </div>
      </div>

      <span aria-hidden className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#F4D64E]/[0.06] blur-3xl pointer-events-none" />
    </div>
  </section>
);

/* ============================================================
   PAGE
============================================================ */
export default function MarketingPageV2() {
  const { open: openContact } = useContactPanel();

  useEffect(() => {
    document.title = "eCommerce Marketing v2 — Sunday Digital";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      data-testid="marketing-page-v2"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <MarketingHero onPrimary={openContact} />
        <TrustStripMkt />
        <WhyUs />
        <MethodV2 />
        <ServicesGrid />
        <Showreel />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}
